import { useMemo, useRef, useState } from 'react'
import { COMPLEXITY_MAX, COMPLEXITY_MIN, TASK_PRIORITY, type TaskPriority } from '@ovrsee/shared'
import { ApiError } from '../../lib/api.js'
import { FormModal } from '../../components/FormModal.js'
import { FieldError, FormError } from '../auth/FormError.js'
import { useTasks, useAssignableUsers, useCreateTask, useWorkload } from './useTasks.js'
import { useCreateTemplate, useTemplates, type TemplateItem } from './useTemplates.js'
import { useTaskAttachments } from './useTaskAttachments.js'
import { Button, Input, Select, TextArea } from '../../ui/index.js'

const HEAVY_LOAD_THRESHOLD = 8
/**
 * width + minWidth + border-box are not cosmetic. A `datetime-local` input has a
 * ~219px intrinsic minimum in Chrome, so two of them in a two-column grid blow
 * past a 343px modal — and the document's scrollWidth does NOT reveal it,
 * because FormModal's `overflow: auto` absorbs the overflow. Measured at 375px
 * on the live stack, 2026-08-11.
 */
const SELECT_STYLE = {
  padding: '7px 10px',
  borderRadius: 'var(--r-md)',
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--fg)',
  fontSize: 13,
  width: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
} as const

/**
 * Bare `1fr` floors at min-content (the M2 lesson); minmax(0, 1fr) lets a column
 * shrink below its content's intrinsic width. `.field` needs min-width: 0 for
 * the same reason — a grid item defaults to min-width: auto.
 */
const TWO_COLUMN_GRID = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 'var(--o-space-4)',
  marginBottom: 'var(--o-space-4)',
} as const

const FIELD_CELL = { minWidth: 0 } as const

const PRIORITIES: readonly { value: TaskPriority; label: string }[] = [
  { value: TASK_PRIORITY.HIGH, label: 'High' },
  { value: TASK_PRIORITY.MEDIUM, label: 'Medium' },
  { value: TASK_PRIORITY.LOW, label: 'Low' },
]

export function AssignTaskModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('')
  const [assigneeId, setAssigneeId] = useState('')
  const [priority, setPriority] = useState<TaskPriority>(TASK_PRIORITY.MEDIUM)
  const [complexity, setComplexity] = useState(5)
  const [deadline, setDeadline] = useState('')
  const [templateId, setTemplateId] = useState('')
  const [saveAsTemplate, setSaveAsTemplate] = useState('')
  const [cadence, setCadence] = useState('')
  const [weekday, setWeekday] = useState(1)
  const [dayOfMonth, setDayOfMonth] = useState(1)
  const [dueTime, setDueTime] = useState('17:00')
  const [dependsOnTaskId, setDependsOnTaskId] = useState('')
  const [selectedFiles, setSelectedFiles] = useState<readonly File[]>([])
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const submitLock = useRef(false)

  const people = useAssignableUsers()
  const workload = useWorkload(assigneeId === '' ? null : assigneeId)
  const createTask = useCreateTask()
  const templates = useTemplates(isOpen)
  const createTemplate = useCreateTemplate()
  const uploadAttachments = useTaskAttachments()
  // Candidates for "depends on": anything currently open enough to block work.
  const openTasks = useTasks({})

  const deadlineInPast = useMemo(() => {
    if (deadline === '') return false
    const parsed = new Date(deadline)
    return !Number.isNaN(parsed.getTime()) && parsed.getTime() <= Date.now()
  }, [deadline])

  const canSubmit =
    title.trim() !== '' && category.trim() !== '' && assigneeId !== '' && deadline !== '' && !deadlineInPast

  function applyTemplate(id: string) {
    setTemplateId(id)
    const chosen = (templates.data ?? []).find((t: TemplateItem) => t.id === id)
    if (chosen === undefined) return
    // Prefill only: manual use of a template records nothing on the task (spec §2.4).
    setTitle(chosen.title)
    setDescription(chosen.description)
    setCategory(chosen.category)
    setPriority(chosen.priority)
    setComplexity(chosen.complexity)
    if (chosen.defaultAssignee !== null) setAssigneeId(chosen.defaultAssignee.id)
  }

  async function submit() {
    if (submitLock.current) return
    submitLock.current = true
    setFieldErrors({})
    setFormError(null)
    let succeeded = false
    try {
      const created = await createTask.mutateAsync({
        title: title.trim(),
        description: description.trim(),
        category: category.trim(),
        assigneeId,
        priority,
        complexity,
        // datetime-local gives a local wall-clock string; the API wants ISO with an offset.
        deadlineAt: new Date(deadline).toISOString(),
        ...(dependsOnTaskId === '' ? {} : { dependsOnTaskId }),
      })
      if (saveAsTemplate.trim() !== '') {
        await createTemplate.mutateAsync({
          name: saveAsTemplate.trim(),
          title: title.trim(),
          description: description.trim(),
          category: category.trim(),
          priority,
          complexity,
          ...(cadence === ''
            ? {}
            : {
                cadence,
                dueTime,
                assigneeId,
                ...(cadence === 'WEEKLY' ? { weekday } : {}),
                ...(cadence === 'MONTHLY' ? { dayOfMonth } : {}),
          }),
        })
      }
      if (selectedFiles.length > 0) {
        setUploading(true)
        const results = await uploadAttachments(created.id, selectedFiles)
        setUploading(false)
        const failures = results.filter((result) => !result.ok)
        if (failures.length > 0) {
          setFormError(
            failures
              .map((result) => `${result.fileName}: ${result.message ?? 'Upload failed'}`)
              .join(' · '),
          )
          return
        }
      }
      succeeded = true
      onClose()
    } catch (error) {
      if (error instanceof ApiError && error.isValidation && error.details !== undefined) {
        setFieldErrors(error.details)
      } else if (error instanceof ApiError) {
        setFormError(error.message)
      } else {
        setFormError('Could not assign that task. Try again.')
      }
      // Deliberately does NOT close: the manager would lose everything they typed.
    } finally {
      setUploading(false)
      if (!succeeded) submitLock.current = false
    }
  }

  return (
      <FormModal
      title="Assign task"
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={() => void submit()}
      submitLabel="Assign task"
      canSubmit={canSubmit}
      busy={createTask.isPending || createTemplate.isPending || uploading}
    >
      <FormError message={formError} />

      <div className="field" style={{ marginBottom: 'var(--o-space-4)' }}>
        <label className="field-label" htmlFor="task-title">
          Task title
        </label>
        <Input id="task-title" value={title} onChange={setTitle} aria-label="Task title" />
        <FieldError message={fieldErrors['title']} />
      </div>

      <div className="field" style={{ marginBottom: 'var(--o-space-4)' }}>
        <label className="field-label" htmlFor="task-description">
          Description
        </label>
        <TextArea
          id="task-description"
          value={description}
          onChange={setDescription}
          aria-label="Description"
          rows={3}
        />
        <FieldError message={fieldErrors['description']} />
      </div>

      <div style={TWO_COLUMN_GRID}>
        <div className="field" style={FIELD_CELL}>
          <label className="field-label" htmlFor="task-category">
            Category
          </label>
          <Input id="task-category" value={category} onChange={setCategory} aria-label="Category" />
          <FieldError message={fieldErrors['category']} />
        </div>

        <div className="field" style={FIELD_CELL}>
          <label className="field-label" htmlFor="task-assignee">
            Assignee
          </label>
          <Select
            id="task-assignee"
            aria-label="Assignee"
            value={assigneeId}
            onChange={setAssigneeId}
            style={SELECT_STYLE}
          >
            <option value="">Choose a person</option>
            {(people.data ?? []).map((person) => (
              <option key={person.id} value={person.id}>
                {person.name}
              </option>
            ))}
          </Select>
          <FieldError message={fieldErrors['assigneeId']} />
        </div>
      </div>

      {/* PRD §6.1: the manager must see the existing load BEFORE assigning. */}
      {workload.isSuccess ? (
        <div
          style={{
            background: 'var(--bg-subtle)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-md)',
            padding: 'var(--o-space-3) var(--o-space-4)',
            marginBottom: 'var(--o-space-4)',
            fontSize: 12.5,
            color: 'var(--fg-2)',
          }}
        >
          <div style={{ fontWeight: 500 }}>{workload.data.activeCount} active tasks</div>
          <div style={{ color: 'var(--fg-muted)' }}>
            {workload.data.dueNext24h} due in the next 24 hours · {workload.data.dueNext7d} due this week
            {workload.data.blocked > 0 ? ` · ${workload.data.blocked} blocked` : ''}
          </div>
          {workload.data.leave !== null ? (
            <div style={{ marginTop: 4, color: 'var(--fg-muted)' }}>
              {workload.data.leave.current ? 'On leave now' : 'Upcoming leave'}: {workload.data.leave.type.toLowerCase()} ·{' '}
              {workload.data.leave.startsAt.slice(0, 10)} to {workload.data.leave.endsAt.slice(0, 10)}
            </div>
          ) : null}
          {workload.data.activeCount >= HEAVY_LOAD_THRESHOLD ? (
            <div style={{ color: 'var(--warning)', marginTop: 4 }}>
              This person is already carrying a heavy load.
            </div>
          ) : null}
        </div>
      ) : null}

      <div style={TWO_COLUMN_GRID}>
        <div className="field" style={FIELD_CELL}>
          <label className="field-label" htmlFor="task-complexity">
            Complexity
          </label>
          <Input
            id="task-complexity"
            aria-label="Complexity"
            type="range"
            min={COMPLEXITY_MIN}
            max={COMPLEXITY_MAX}
            value={`${complexity}`}
            onChangeEvent={(event) => setComplexity(Number(event.target.value))}
            style={{ width: '100%', minWidth: 0 }}
          />
          <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{complexity}</div>
          <FieldError message={fieldErrors['complexity']} />
        </div>

        <div className="field" style={FIELD_CELL}>
          <label className="field-label" htmlFor="task-deadline">
            Deadline
          </label>
          <Input
            id="task-deadline"
            aria-label="Deadline"
            type="datetime-local"
            value={deadline}
            onChange={setDeadline}
            style={SELECT_STYLE}
          />
          <FieldError message={deadlineInPast ? 'Deadline must be in the future.' : fieldErrors['deadlineAt']} />
        </div>
      </div>

      <div className="field" style={{ marginBottom: 'var(--o-space-4)' }}>
        <label className="field-label" htmlFor="task-priority">
          Priority
        </label>
        <div role="group" aria-label="Priority" className="row-actions">
          {PRIORITIES.map((option) => {
            const active = option.value === priority
            return (
              <Button
                key={option.value}
                type="button"
                kind={active ? 'primary' : 'secondary'}
                size="small"
                aria-pressed={active}
                onClick={() => setPriority(option.value)}
              >
                {option.label}
              </Button>
            )
          })}
        </div>
      </div>

      {(templates.data ?? []).length > 0 ? (
        <div className="field" style={{ marginBottom: 'var(--o-space-4)' }}>
          <label className="field-label" htmlFor="task-template">
            Start from template
          </label>
          <Select
            id="task-template"
            aria-label="Start from template"
            value={templateId}
            onChange={applyTemplate}
            style={SELECT_STYLE}
          >
            <option value="">Blank task</option>
            {(templates.data ?? []).map((option: TemplateItem) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </Select>
        </div>
      ) : null}

      <div className="field" style={{ marginBottom: 'var(--o-space-4)' }}>
        <label className="field-label" htmlFor="task-depends-on">
          Depends on
        </label>
        <Select
          id="task-depends-on"
          aria-label="Depends on"
          value={dependsOnTaskId}
          onChange={setDependsOnTaskId}
          style={SELECT_STYLE}
        >
          <option value="">Nothing - can start immediately</option>
          {(openTasks.data?.items ?? []).map((option) => (
            <option key={option.id} value={option.id}>
              {option.reference} · {option.title}
            </option>
          ))}
        </Select>
        <FieldError message={fieldErrors['dependsOnTaskId']} />
      </div>

      <div className="field" style={{ marginBottom: 'var(--o-space-4)' }}>
        <label className="field-label" htmlFor="task-save-template">
          Save as template
        </label>
        <Input
          id="task-save-template"
          value={saveAsTemplate}
          onChange={setSaveAsTemplate}
          aria-label="Save as template"
          placeholder="Template name (leave blank to skip)"
        />
      </div>

      <div className="field" style={{ marginBottom: 'var(--o-space-4)' }}>
        <label className="field-label" htmlFor="task-attachments">
          Attachments
        </label>
        <input
          id="task-attachments"
          aria-label="Attachments"
          className="ui-control"
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.png,.jpeg,.jpg,.gif"
          onChange={(event) => {
            setFormError(null)
            setSelectedFiles(Array.from(event.target.files ?? []))
          }}
        />
        {selectedFiles.length > 0 ? (
          <div style={{ marginTop: 'var(--o-space-2)', fontSize: 12.5, color: 'var(--fg-muted)' }}>
            {selectedFiles.map((file) => file.name).join(', ')}
          </div>
        ) : null}
      </div>

      {saveAsTemplate.trim() !== '' ? (
        <div className="field" style={{ marginBottom: 'var(--o-space-4)' }}>
          <label className="field-label" htmlFor="task-cadence">
            Repeat
          </label>
          <Select
            id="task-cadence"
            aria-label="Repeat"
            value={cadence}
            onChange={setCadence}
            style={SELECT_STYLE}
          >
            <option value="">Do not repeat</option>
            <option value="DAILY">Every day</option>
            <option value="WEEKLY">Every week</option>
            <option value="MONTHLY">Every month</option>
          </Select>

          {cadence === 'WEEKLY' ? (
            <Select
              aria-label="Weekday"
              value={`${weekday}`}
              onChange={(value) => setWeekday(Number(value))}
              style={{ ...SELECT_STYLE, marginTop: 'var(--o-space-2)' }}
            >
              {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(
                (label, index) => (
                  <option key={label} value={index + 1}>
                    {label}
                  </option>
                ),
              )}
            </Select>
          ) : null}

          {cadence === 'MONTHLY' ? (
            <Input
              aria-label="Day of month"
              type="number"
              min={1}
              max={31}
              value={`${dayOfMonth}`}
              onChangeEvent={(event) => setDayOfMonth(Number(event.target.value))}
              style={{ ...SELECT_STYLE, marginTop: 'var(--o-space-2)' }}
            />
          ) : null}

          {cadence !== '' ? (
            <Input
              aria-label="Due time"
              type="time"
              value={dueTime}
              onChange={setDueTime}
              style={{ ...SELECT_STYLE, marginTop: 'var(--o-space-2)' }}
            />
          ) : null}
        </div>
      ) : null}
    </FormModal>
  )
}
